import {
  Alert,
  Box,
  MenuItem,
  Stack,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, useMemo, type ChangeEvent } from "react";
import {
  BenchTenderShape,
  useOutboundBenchServiceLevelsQuery,
} from "../../../generated/graphql";
import {
  APPOINTMENT_DAY_NOTE,
  type BenchOrderSchedule,
} from "../create-orders-schedule";

const styles = {
  // Row gap leaves room for each field's floating label, so a label never
  // sits on the field above it.
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 240px)",
    columnGap: 2,
    rowGap: 3,
  } satisfies SxProps<Theme>,
  serviceLevel: { width: 496 } satisfies SxProps<Theme>,
};

const LABEL_SHRINK = { shrink: true };

const NO_SERVICE_LEVEL = "";

type ScheduleField = Exclude<keyof BenchOrderSchedule, "serviceLevelCode">;

type CreateOrdersScheduleFieldsProps = {
  readonly partnershipUuid: string | null;
  readonly shape: BenchTenderShape;
  readonly schedule: BenchOrderSchedule;
  readonly onChange: (next: BenchOrderSchedule) => void;
};

/**
 * The service level, dates and appointment windows of new test orders. The
 * service-level list holds the partnership's mapped codes only, since the EDI
 * inbound handler resolves no other code.
 */
const CreateOrdersScheduleFields = ({
  partnershipUuid,
  shape,
  schedule,
  onChange,
}: CreateOrdersScheduleFieldsProps) => {
  const { data, error } = useOutboundBenchServiceLevelsQuery({
    errorPolicy: "none",
    skip: partnershipUuid === null,
    variables: { partnershipUuid: partnershipUuid ?? "" },
  });
  const serviceLevels = useMemo(
    () => data?.outboundBenchServiceLevels ?? [],
    [data],
  );

  const handleServiceLevelChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange({ ...schedule, serviceLevelCode: event.target.value });
    },
    [onChange, schedule],
  );
  const handleFieldChange = useCallback(
    (field: ScheduleField) =>
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange({ ...schedule, [field]: event.target.value });
      },
    [onChange, schedule],
  );
  const handlers = useMemo(
    () => ({
      pickupDate: handleFieldChange("pickupDate"),
      pickupStart: handleFieldChange("pickupStart"),
      pickupEnd: handleFieldChange("pickupEnd"),
      deliveryDate: handleFieldChange("deliveryDate"),
      deliveryStart: handleFieldChange("deliveryStart"),
      deliveryEnd: handleFieldChange("deliveryEnd"),
    }),
    [handleFieldChange],
  );

  const hasPickup = shape !== BenchTenderShape.Delivery;
  const hasDelivery = shape !== BenchTenderShape.Pickup;

  return (
    <Stack spacing={3}>
      <Box>
        <TextField
          select
          helperText={
            serviceLevels.length === 0 && data !== undefined
              ? "The partnership maps no service level codes."
              : "Only codes the partnership maps to a service."
          }
          label="Service level"
          sx={styles.serviceLevel}
          value={schedule.serviceLevelCode}
          onChange={handleServiceLevelChange}
        >
          <MenuItem value={NO_SERVICE_LEVEL}>None</MenuItem>
          {serviceLevels.map((level) => (
            <MenuItem key={level.code} value={level.code}>
              {level.code} ({level.serviceName})
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box sx={styles.grid}>
        {hasPickup && (
          <>
            <TextField
              fullWidth
              label="Pickup date"
              InputLabelProps={LABEL_SHRINK}
              type="date"
              value={schedule.pickupDate}
              onChange={handlers.pickupDate}
            />
            <TextField
              fullWidth
              label="Pickup appointment from"
              InputLabelProps={LABEL_SHRINK}
              type="time"
              value={schedule.pickupStart}
              onChange={handlers.pickupStart}
            />
            <TextField
              fullWidth
              label="Pickup appointment to"
              InputLabelProps={LABEL_SHRINK}
              type="time"
              value={schedule.pickupEnd}
              onChange={handlers.pickupEnd}
            />
          </>
        )}
        {hasDelivery && (
          <>
            <TextField
              fullWidth
              label="Delivery date"
              InputLabelProps={LABEL_SHRINK}
              type="date"
              value={schedule.deliveryDate}
              onChange={handlers.deliveryDate}
            />
            <TextField
              fullWidth
              label="Delivery appointment from"
              InputLabelProps={LABEL_SHRINK}
              type="time"
              value={schedule.deliveryStart}
              onChange={handlers.deliveryStart}
            />
            <TextField
              fullWidth
              label="Delivery appointment to"
              InputLabelProps={LABEL_SHRINK}
              type="time"
              value={schedule.deliveryEnd}
              onChange={handlers.deliveryEnd}
            />
          </>
        )}
      </Box>
      <Typography color="text.secondary" variant="caption">
        {APPOINTMENT_DAY_NOTE}
      </Typography>
      {error !== undefined && (
        <Alert severity="error">
          Could not load the service levels: {error.message}
        </Alert>
      )}
    </Stack>
  );
};

export default CreateOrdersScheduleFields;
